import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2, Star } from "lucide-react";
import { fetchSettings, patchSettings } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import ImageUpload from "../ImageUpload";
import { toast } from "../../hooks/use-toast";

const input = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 outline-none transition-colors";
const label = "block text-xs text-slate-400 mb-1.5";

const ReviewsPanel = () => {
  const { reload } = useSite();
  const [reviews, setReviews] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings().then((s) => setReviews(s.reviews || [])).catch(() => {}); }, []);
  if (!reviews) return <div className="text-slate-400">Loading...</div>;

  const upd = (i, k, v) => setReviews((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setReviews((p) => [...p, { name: "", handle: "", rating: 5, text: "", avatar: "", initials: "" }]);
  const remove = (i) => setReviews((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = reviews.map((r) => ({ ...r, rating: Number(r.rating) || 5, initials: r.initials || (r.name || "?").slice(0, 2).toUpperCase() }));
      await patchSettings({ reviews: clean });
      await reload();
      toast({ title: "Saved", description: "Reviews updated." });
    } catch { toast({ title: "Error", description: "Could not save." }); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h3 className="text-white font-bold text-lg">Customer Reviews</h3><p className="text-slate-400 text-sm">Add names, star ratings, text and optional photos.</p></div>
        <button onClick={add} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-4 py-2.5 rounded-xl transition-colors"><Plus size={16} /> Add Review</button>
      </div>
      <div className="space-y-4">
        {reviews.map((r, i) => (
          <div key={i} className="bg-navy-800 rounded-2xl border border-white/10 p-5">
            <div className="grid sm:grid-cols-12 gap-3">
              <div className="sm:col-span-3"><label className={label}>Name</label><input className={input} value={r.name || ""} onChange={(e) => upd(i, "name", e.target.value)} /></div>
              <div className="sm:col-span-3"><label className={label}>Handle (optional)</label><input className={input} value={r.handle || ""} onChange={(e) => upd(i, "handle", e.target.value)} placeholder="@username" /></div>
              <div className="sm:col-span-2"><label className={label}>Rating</label><select className={input} value={r.rating || 5} onChange={(e) => upd(i, "rating", e.target.value)}>{[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} star{n>1?"s":""}</option>)}</select></div>
              <div className="sm:col-span-4"><label className={label}>Photo (optional)</label><ImageUpload value={r.avatar} onUpload={(url) => upd(i, "avatar", url)} onClear={() => upd(i, "avatar", "")} label="Upload photo" /></div>
              <div className="sm:col-span-12"><label className={label}>Review Text</label><textarea className={`${input} h-20 resize-none`} value={r.text || ""} onChange={(e) => upd(i, "text", e.target.value)} /></div>
            </div>
            <div className="flex justify-between items-center mt-3">
              <div className="flex">{[...Array(Number(r.rating) || 5)].map((_, x) => <Star key={x} size={14} className="fill-gold text-gold" />)}</div>
              <button onClick={() => remove(i)} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={15} /> Remove</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="mt-6 inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>
    </div>
  );
};

export default ReviewsPanel;
